import React from "react";
import { motion } from "framer-motion";
import { Rocket, Check, Sparkles } from "lucide-react";

const migrationPartners = [
  { name: "Dentrix", logo: "https://framerusercontent.com/images/FK2PxMGUliSNEptVBpWhWGjhW4.png" },
  { name: "Eaglesoft", logo: "/assets/dental-software-logos/pms/eaglesoft.png" },
  { name: "Open Dental", logo: "/assets/dental-software-logos/pms/opendental.png" },
  { name: "Curve Dental", logo: "/assets/additional-dental-logos/pms/curve_dental.jpg" },
  { name: "Dentrix Ascend" },
  { name: "Denticon", logo: "/assets/additional-dental-logos/pms/denticon.png" },
  { name: "DentiMax", logo: "/assets/additional-dental-logos/pms/dentimax.webp" },
  { name: "Dolphin" },
];

const migrationHighlights = [
  "Zero-downtime migration — we cut over on a weekend",
  "Full data QA — patients, ledgers, notes, images",
  "White-glove onboarding included",
  "Mapping & validation handled by our team",
];

const MigrationPartnersSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient blue gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #93c5fd 50%, #dbeafe 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #bfdbfe 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1120px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-7"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-3 border border-blue-100/60 shadow-sm">
            <Rocket className="w-4 h-4" />
            <span>PMS Migrations</span>
          </div>
          <h2 className="text-3xl lg:text-[42px] font-bold text-slate-900 tracking-tight leading-tight">
            We can migrate you from{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              nearly any PMS.
            </span>
          </h2>
          <p className="text-slate-900 text-[22px] font-semibold mt-6 max-w-2xl mx-auto leading-relaxed">
            Our migration team has moved practices off most legacy systems. If yours isn't
            shown, chances are we've seen it before.
          </p>
        </motion.div>

        {/* Logo grid — 3x3, 8 named PMSs + "many more" */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="w-full grid grid-cols-3 gap-4 mb-6"
        >
          {migrationPartners.map((p, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-slate-200 px-4 py-5 flex flex-col items-center justify-center gap-2 hover:border-blue-200 hover:shadow-md transition-all min-h-[130px]"
            >
              {p.logo ? (
                <>
                  <div className="h-[54px] flex items-center justify-center">
                    <img
                      src={p.logo}
                      alt={p.name}
                      className="max-h-[54px] max-w-[80%] object-contain"
                    />
                  </div>
                  <p className="text-[13px] font-semibold text-slate-700">{p.name}</p>
                </>
              ) : (
                <p className="text-[20px] font-bold text-slate-700 tracking-tight text-center">
                  {p.name}
                </p>
              )}
            </div>
          ))}

          {/* "Many more" card */}
          <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50/70 via-white to-indigo-50/50 px-4 py-5 flex flex-col items-center justify-center gap-1.5 min-h-[130px]">
            <Sparkles className="w-5 h-5 text-blue-500" />
            <span className="text-[18px] font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              & many more
            </span>
            <p className="text-[11px] text-slate-500 text-center leading-snug">
              Oryx, Carestack, Tab32, Archy…
            </p>
          </div>
        </motion.div>

        {/* Migration process highlights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.5 }}
          className="w-full rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50/60 via-white to-indigo-50/40 p-5"
        >
          <div className="grid grid-cols-4 gap-x-5 gap-y-2">
            {migrationHighlights.map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-blue-600" />
                </div>
                <span className="text-[14px] text-slate-700 font-medium leading-snug">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        <p className="mt-4 text-[14px] text-slate-500 text-center font-medium">
          Don't see your system? Ask us — we support custom migrations on a case-by-case basis.
        </p>
      </div>
    </div>
  );
};

export default MigrationPartnersSlide;
